/**
 * Post-processing: HDR scene target -> bloom -> composite to the canvas.
 *
 * The scene's ShaderMaterials write LINEAR HDR radiance (sun disc is ~20+),
 * so the scene goes into a half-float target first. Bloom is the dual-filter
 * ("Call of Duty: Advanced Warfare") chain: soft-knee bright-pass into a
 * half-res mip, 13-tap downsamples, then 3x3 tent upsamples blended additively
 * back up the same mips. The composite pass does exposure, ACES tonemap,
 * saturation/contrast grade, sRGB encode and dithering.
 *
 * All passes are RawShaderMaterials: three injects nothing but #version and
 * the defines, so there is nothing to collide with (ShaderMaterial would
 * prepend its own precision/attribute/colorspace helper declarations).
 */

import * as THREE from 'three';

export interface PostOptions {
  bloom: boolean;
  msaa: number; // 0, 2 or 4 samples
}

/** Half-res bright-pass mip + 5 further downsamples. */
const BLOOM_MIPS = 6;

const FS_VERT = /* glsl */ `
precision highp float;
in vec3 position;
out vec2 vUv;
void main() {
  vUv = position.xy * 0.5 + 0.5;
  gl_Position = vec4(position.xy, 0.0, 1.0);
}
`;

// 13-tap downsample (Jimenez 2014). With PREFILTER it also applies the
// soft-knee threshold and a Karis average so tiny very-bright features (sun
// disc edge, lava sparkle) do not flicker as they cross texel boundaries.
const DOWN_FRAG = /* glsl */ `
precision highp float;

uniform sampler2D uSrc;
uniform vec2 uTexel;       // 1 / source size
#ifdef PREFILTER
uniform float uThreshold;  // linear luminance
#endif

in vec2 vUv;
out vec4 fragColor;

vec3 tap(float x, float y) {
  vec3 c = texture(uSrc, vUv + vec2(x, y) * uTexel).rgb;
#ifdef PREFILTER
  // clamp: one inf/huge texel would otherwise smear over the whole chain
  c = clamp(c, 0.0, 100.0);
#endif
  return c;
}

#ifdef PREFILTER
float luma(vec3 c) { return dot(c, vec3(0.2126, 0.7152, 0.0722)); }

vec3 bright(vec3 c) {
  float l = luma(c);
  float knee = uThreshold * 0.5;
  float soft = clamp(l - uThreshold + knee, 0.0, 2.0 * knee);
  soft = soft * soft / (4.0 * knee + 1e-5);
  return c * (max(soft, l - uThreshold) / max(l, 1e-4));
}
#endif

void main() {
  vec3 a = tap(-2.0,  2.0), b = tap(0.0,  2.0), c = tap(2.0,  2.0);
  vec3 d = tap(-2.0,  0.0), e = tap(0.0,  0.0), f = tap(2.0,  0.0);
  vec3 g = tap(-2.0, -2.0), h = tap(0.0, -2.0), i = tap(2.0, -2.0);
  vec3 j = tap(-1.0,  1.0), k = tap(1.0,  1.0);
  vec3 l = tap(-1.0, -1.0), m = tap(1.0, -1.0);

#ifdef PREFILTER
  // five overlapping 2x2 boxes, each thresholded then weighted by 1/(1+luma)
  vec3 g0 = bright((j + k + l + m) * 0.25);
  vec3 g1 = bright((a + b + d + e) * 0.25);
  vec3 g2 = bright((b + c + e + f) * 0.25);
  vec3 g3 = bright((d + e + g + h) * 0.25);
  vec3 g4 = bright((e + f + h + i) * 0.25);
  float w0 = 0.5   / (1.0 + luma(g0));
  float w1 = 0.125 / (1.0 + luma(g1));
  float w2 = 0.125 / (1.0 + luma(g2));
  float w3 = 0.125 / (1.0 + luma(g3));
  float w4 = 0.125 / (1.0 + luma(g4));
  vec3 sum = g0 * w0 + g1 * w1 + g2 * w2 + g3 * w3 + g4 * w4;
  fragColor = vec4(sum / (w0 + w1 + w2 + w3 + w4), 1.0);
#else
  vec3 sum = e * 0.125
           + (a + c + g + i) * 0.03125
           + (b + d + f + h) * 0.0625
           + (j + k + l + m) * 0.125;
  fragColor = vec4(sum, 1.0);
#endif
}
`;

// 3x3 tent upsample; drawn with ONE/ONE blending onto the next larger mip.
const UP_FRAG = /* glsl */ `
precision highp float;

uniform sampler2D uSrc;
uniform vec2 uTexel; // 1 / source size

in vec2 vUv;
out vec4 fragColor;

void main() {
  vec4 o = uTexel.xyxy * vec4(1.0, 1.0, -1.0, 0.0);
  vec3 s = texture(uSrc, vUv).rgb * 4.0;
  s += (texture(uSrc, vUv - o.xw).rgb + texture(uSrc, vUv + o.xw).rgb
      + texture(uSrc, vUv - o.wy).rgb + texture(uSrc, vUv + o.wy).rgb) * 2.0;
  s += texture(uSrc, vUv - o.xy).rgb + texture(uSrc, vUv + o.xy).rgb
     + texture(uSrc, vUv - o.zy).rgb + texture(uSrc, vUv + o.zy).rgb;
  fragColor = vec4(s * 0.0625, 1.0);
}
`;

const COMPOSITE_FRAG = /* glsl */ `
precision highp float;

uniform sampler2D uScene;
#ifdef USE_BLOOM
uniform sampler2D uBloom;
uniform float uBloomStrength; // already divided by the mip count
#endif
uniform vec3 uTint;
uniform float uExposure;
uniform float uSaturation;
uniform float uContrast;
uniform vec2 uWobble; // nether portal screen distortion: (strength 0..1, time s)

in vec2 vUv;
out vec4 fragColor;

// ACES fitted (Stephen Hill): sRGB -> RRT_SAT, RRT+ODT fit, ODT_SAT -> sRGB.
const mat3 ACES_IN = mat3(
  0.59719, 0.07600, 0.02840,
  0.35458, 0.90834, 0.13383,
  0.04823, 0.01566, 0.83777);
const mat3 ACES_OUT = mat3(
   1.60475, -0.10208, -0.00327,
  -0.53108,  1.10813, -0.07276,
  -0.07367, -0.00605,  1.07602);

vec3 rrtOdtFit(vec3 v) {
  vec3 a = v * (v + 0.0245786) - 0.000090537;
  vec3 b = v * (0.983729 * v + 0.4329510) + 0.238081;
  return a / b;
}

vec3 tonemap(vec3 c) {
  // 1/0.6 pre-gain keeps mid-tones roughly where they were authored (the raw
  // fit is ~40% darker), so block textures at full light stay bright
  c = ACES_IN * (c / 0.6);
  c = rrtOdtFit(c);
  return clamp(ACES_OUT * c, 0.0, 1.0);
}

vec3 srgbEncode(vec3 c) {
  vec3 lo = c * 12.92;
  vec3 hi = 1.055 * pow(c, vec3(1.0 / 2.4)) - 0.055;
  return mix(lo, hi, step(vec3(0.0031308), c));
}

// interleaved gradient noise (Jimenez): cheap and stable per pixel
float ign(vec2 p) {
  return fract(52.9829189 * fract(dot(p, vec2(0.06711056, 0.00583715))));
}

void main() {
  vec2 uv = vUv;
  if (uWobble.x > 0.0) {
    float t = uWobble.y;
    uv += uWobble.x * 0.012 * vec2(sin(vUv.y * 9.0 + t * 2.3), cos(vUv.x * 7.0 + t * 1.9));
    uv = clamp(uv, vec2(0.0), vec2(1.0));
  }
  vec3 c = texture(uScene, uv).rgb;
#ifdef USE_BLOOM
  c += texture(uBloom, uv).rgb * uBloomStrength;
#endif
  c = max(c, 0.0) * uTint * uExposure;

  c = tonemap(c);

  // saturation in linear display space so the luma weights are exact; ACES
  // pulls bright colours toward white and this puts the punch back
  float l = dot(c, vec3(0.2126, 0.7152, 0.0722));
  c = clamp(mix(vec3(l), c, uSaturation), 0.0, 1.0);

  c = srgbEncode(c);

  // contrast on encoded values: 0.5 is perceptual mid-grey only after encode
  c = clamp((c - 0.5) * uContrast + 0.5, 0.0, 1.0);

  // +-0.5 LSB of noise hides 8-bit banding in the sky gradient
  c += (ign(gl_FragCoord.xy) - 0.5) / 255.0;

  fragColor = vec4(c, 1.0);
}
`;

function fsMaterial(
  fragmentShader: string,
  uniforms: Record<string, THREE.IUniform>,
  defines: Record<string, string> = {},
): THREE.RawShaderMaterial {
  return new THREE.RawShaderMaterial({
    glslVersion: THREE.GLSL3,
    vertexShader: FS_VERT,
    fragmentShader,
    uniforms,
    defines,
    depthTest: false,
    depthWrite: false,
    toneMapped: false,
  });
}

export class PostFX {
  exposure = 1.0;
  saturation = 1.0;
  contrast = 1.0;
  bloomStrength = 0.35;
  bloomThreshold = 1.0;
  /** Multiplied into the final colour before tonemapping (e.g. underwater tint). Default white. */
  readonly tint = new THREE.Color(1, 1, 1);
  /** Portal wobble: x = strength 0..1, y = animation time (s). See game/dimensionTravel.ts. */
  readonly wobble = new THREE.Vector2(0, 0);

  private readonly renderer: THREE.WebGLRenderer;
  private readonly texType: THREE.TextureDataType;
  private bloom: boolean;
  private width = 1;
  private height = 1;

  private readonly sceneRT: THREE.WebGLRenderTarget;
  private readonly mips: THREE.WebGLRenderTarget[] = [];

  private readonly fsScene = new THREE.Scene();
  private readonly fsCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  private readonly fsGeometry = new THREE.BufferGeometry();
  private readonly fsMesh: THREE.Mesh;

  private readonly prefilterMat: THREE.RawShaderMaterial;
  private readonly downMat: THREE.RawShaderMaterial;
  private readonly upMat: THREE.RawShaderMaterial;
  private readonly compositeMat: THREE.RawShaderMaterial;

  private readonly prefilterU = {
    uSrc: { value: null as THREE.Texture | null },
    uTexel: { value: new THREE.Vector2() },
    uThreshold: { value: 1.0 },
  };
  private readonly downU = {
    uSrc: { value: null as THREE.Texture | null },
    uTexel: { value: new THREE.Vector2() },
  };
  private readonly upU = {
    uSrc: { value: null as THREE.Texture | null },
    uTexel: { value: new THREE.Vector2() },
  };
  private readonly compositeU: {
    uScene: { value: THREE.Texture | null };
    uBloom: { value: THREE.Texture | null };
    uBloomStrength: { value: number };
    uTint: { value: THREE.Color };
    uExposure: { value: number };
    uSaturation: { value: number };
    uContrast: { value: number };
    uWobble: { value: THREE.Vector2 };
  };

  constructor(renderer: THREE.WebGLRenderer, opts: PostOptions) {
    this.renderer = renderer;
    this.bloom = opts.bloom;

    // WebGL2 only renders to RGBA16F with one of these extensions (three
    // requests both at init). Without them fall back to LDR rather than an
    // incomplete framebuffer.
    const ext = renderer.extensions;
    const hdr = ext.has('EXT_color_buffer_half_float') || ext.has('EXT_color_buffer_float');
    this.texType = hdr ? THREE.HalfFloatType : THREE.UnsignedByteType;

    this.sceneRT = new THREE.WebGLRenderTarget(1, 1, {
      type: this.texType,
      format: THREE.RGBAFormat,
      colorSpace: THREE.LinearSRGBColorSpace,
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      generateMipmaps: false,
      depthBuffer: true,
      stencilBuffer: false,
      samples: sanitizeSamples(opts.msaa),
      // nothing reads scene depth afterwards; skip the extra MSAA depth blit
      resolveDepthBuffer: false,
    });

    for (let i = 0; i < BLOOM_MIPS; i++) {
      this.mips.push(
        new THREE.WebGLRenderTarget(1, 1, {
          type: this.texType,
          format: THREE.RGBAFormat,
          colorSpace: THREE.LinearSRGBColorSpace,
          minFilter: THREE.LinearFilter,
          magFilter: THREE.LinearFilter,
          generateMipmaps: false,
          depthBuffer: false,
          stencilBuffer: false,
        }),
      );
    }

    // One oversized triangle: no diagonal seam, no overdraw along it.
    this.fsGeometry.setAttribute(
      'position',
      new THREE.BufferAttribute(new Float32Array([-1, -1, 0, 3, -1, 0, -1, 3, 0]), 3),
    );

    this.prefilterMat = fsMaterial(DOWN_FRAG, this.prefilterU, { PREFILTER: '' });
    this.downMat = fsMaterial(DOWN_FRAG, this.downU);
    this.upMat = fsMaterial(UP_FRAG, this.upU);
    this.upMat.blending = THREE.CustomBlending;
    this.upMat.blendEquation = THREE.AddEquation;
    this.upMat.blendSrc = THREE.OneFactor;
    this.upMat.blendDst = THREE.OneFactor;

    this.compositeU = {
      uScene: { value: this.sceneRT.texture },
      uBloom: { value: this.mips[0].texture },
      uBloomStrength: { value: 0 },
      uTint: { value: this.tint },
      uExposure: { value: 1 },
      uSaturation: { value: 1 },
      uContrast: { value: 1 },
      uWobble: { value: this.wobble },
    };
    this.compositeMat = fsMaterial(COMPOSITE_FRAG, this.compositeU, this.bloom ? { USE_BLOOM: '' } : {});

    this.fsMesh = new THREE.Mesh(this.fsGeometry, this.compositeMat);
    this.fsMesh.frustumCulled = false;
    this.fsScene.add(this.fsMesh);

    const size = renderer.getDrawingBufferSize(new THREE.Vector2());
    this.setSize(size.x, size.y);
  }

  setOptions(opts: PostOptions): void {
    const samples = sanitizeSamples(opts.msaa);
    if (samples !== this.sceneRT.samples) {
      // three reads .samples when it (re)builds the framebuffers, so dropping
      // the GL objects is enough; the texture object (and uniforms) survive
      this.sceneRT.dispose();
      this.sceneRT.samples = samples;
    }

    if (opts.bloom !== this.bloom) {
      this.bloom = opts.bloom;
      if (this.bloom) {
        this.compositeMat.defines.USE_BLOOM = '';
      } else {
        delete this.compositeMat.defines.USE_BLOOM;
        // free the chain's GPU memory; three re-allocates lazily if re-enabled
        for (const rt of this.mips) rt.dispose();
      }
      this.compositeMat.needsUpdate = true;
    }
  }

  /** Size in drawing-buffer pixels (CSS size * pixel ratio). */
  setSize(width: number, height: number): void {
    const w = Math.max(1, Math.floor(width) || 1);
    const h = Math.max(1, Math.floor(height) || 1);
    if (w === this.width && h === this.height && this.sceneRT.width === w && this.sceneRT.height === h) return;
    this.width = w;
    this.height = h;

    this.sceneRT.setSize(w, h);
    let mw = w;
    let mh = h;
    for (const rt of this.mips) {
      mw = Math.max(1, mw >> 1);
      mh = Math.max(1, mh >> 1);
      rt.setSize(mw, mh);
    }
  }

  render(scene: THREE.Scene, camera: THREE.Camera): void {
    const r = this.renderer;
    const prevAutoClear = r.autoClear;

    // The HDR target is ours, so it must be cleared here even if the caller
    // runs with autoClear off and clears the canvas by hand.
    r.autoClear = true;
    r.setRenderTarget(this.sceneRT);
    r.render(scene, camera); // MSAA resolve happens at the end of this call

    // Fullscreen passes overwrite every pixel; the upsample passes blend
    // additively and must NOT clear what the downsample left behind.
    r.autoClear = false;

    if (this.bloom) {
      const mips = this.mips;

      this.prefilterU.uSrc.value = this.sceneRT.texture;
      this.prefilterU.uTexel.value.set(1 / this.width, 1 / this.height);
      this.prefilterU.uThreshold.value = Math.max(0, this.bloomThreshold);
      this.pass(this.prefilterMat, mips[0]);

      for (let i = 1; i < BLOOM_MIPS; i++) {
        const src = mips[i - 1];
        this.downU.uSrc.value = src.texture;
        this.downU.uTexel.value.set(1 / src.width, 1 / src.height);
        this.pass(this.downMat, mips[i]);
      }

      for (let i = BLOOM_MIPS - 1; i > 0; i--) {
        const src = mips[i];
        this.upU.uSrc.value = src.texture;
        this.upU.uTexel.value.set(1 / src.width, 1 / src.height);
        this.pass(this.upMat, mips[i - 1]);
      }
    }

    const u = this.compositeU;
    // mips[0] ends up holding the sum of all levels; normalise so strength
    // does not depend on the chain length
    u.uBloomStrength.value = this.bloomStrength / BLOOM_MIPS;
    u.uExposure.value = this.exposure;
    u.uSaturation.value = this.saturation;
    u.uContrast.value = this.contrast;
    this.pass(this.compositeMat, null);

    r.autoClear = prevAutoClear;
  }

  dispose(): void {
    this.sceneRT.dispose();
    for (const rt of this.mips) rt.dispose();
    this.fsGeometry.dispose();
    this.prefilterMat.dispose();
    this.downMat.dispose();
    this.upMat.dispose();
    this.compositeMat.dispose();
  }

  private pass(material: THREE.Material, target: THREE.WebGLRenderTarget | null): void {
    this.fsMesh.material = material;
    this.renderer.setRenderTarget(target);
    this.renderer.render(this.fsScene, this.fsCamera);
  }
}

function sanitizeSamples(msaa: number): number {
  return Math.max(0, Math.floor(msaa) || 0);
}
